<template>
  <Preloader
    v-if="loading"
  />
  <VRow v-else-if="errorMessage">
    <VCol cols="12">
      <VAlert type="error">
        {{ errorMessage }}
      </VAlert>
    </VCol>
  </VRow>
  <VRow v-else class="h-100">
    <VCol cols="12" class="d-flex flex-column">
      <VacancyStepTitle
        v-if="vacancy"
        :progress="80"
        :vacancy="vacancy"
        :step="4"
      >
        {{ $t('step') }}
      </VacancyStepTitle>
      <div class="mobile-container">
        <VacancyFaqForm
          v-model="faqQuestionText"
          :vacancy="vacancy"
          :common-faq="commonFaq"
        />
      </div>

      <div>
        <VBtn
          color="success"
          outlined
          class="mt-3 mt-md-12 mr-3"
          elevation="0"
          @click="back"
        >
          {{ $t('goBack') }}
        </VBtn>
        <Confirm
          :title="$t('questionNotSaved')"
          :cancel-text="$t('continueEditing')"
          :confirm-text="$t('saveDraft')"
          :max-width="500"
          :condition="faqQuestionText !== null && faqQuestionText.length > 0"
          @confirmed="saveDraft"
        >
          <template #activator="{ on: { click }}">
            <VBtn
              color="success"
              outlined
              class="mt-3 mt-md-12 mr-3"
              elevation="0"
              @click="click"
            >
              {{ $t('saveDraft') }}
            </VBtn>
          </template>
        </Confirm>
        <VBtn
          color="success"
          class="mt-3 mt-md-12"
          elevation="0"
          @click="next"
        >
          {{ $t('next') }} &rarr;
        </VBtn>
      </div>
    </VCol>
  </VRow>
</template>

<script>
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle';
import Wizard from '@/orm/models/Wizard/Wizard';
import { wizardSteps, vacancyStatus } from '@/enums';
import VacancyFaqForm from '../../components/Vacancy/VacancyFaqForm.vue';
import Confirm from '../../components/Confirm.vue';
import Preloader from '../../components/Preloader.vue';
import ValidationMixin from '../../mixins/ValidationMixin';
import Vacancy from '../../orm/models/Vacancy/Vacancy';
import VacancyFaqCommon from '../../orm/models/Vacancy/VacancyFaqCommon';

export default {
  name: 'VacancyStepFaq',
  components: {
    VacancyStepTitle,
    Preloader,
    VacancyFaqForm,
    Confirm,
  },
  mixins: [ValidationMixin],
  metaInfo() {
    return {
      title: this.$t('step'),
    };
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      faqQuestionText: null,
      publishLoading: false,
    };
  },
  computed: {
    vacancy() {
      return Vacancy.query().withAllRecursive().find(this.vacancyId);
    },
    hasFaq() {
      return this.vacancy.faq && this.vacancy.faq.length > 0;
    },
    commonFaq() {
      return VacancyFaqCommon.query().all();
    },
    currentInterviewerId() {
      return this.$i18n.locale === 'en' ? 3 : 1;
    },
  },
  watch: {
    vacancy: {
      handler(newVacancy) {
        if (!newVacancy) return;
        if (newVacancy.status === vacancyStatus.ACTIVE) {
          this.$router.push({ name: 'candidates', params: { id: this.vacancyId.toString() } });
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    setState() {
      Wizard
        .setVacancyId(this.vacancyId)
        .setWizardStep(wizardSteps.FAQ);
    },
    async fetchVacancy() {
      await Vacancy.api().fetchById(this.vacancyId);
    },
    async fetchCommonFaq() {
      await VacancyFaqCommon.api().fetch();
    },
    async prepare() {
      this.setState();
      await this.withLoading(async () => {
        await Promise.all([Wizard.fetchData(), this.fetchCommonFaq(), this.fetchVacancy()]);
        if (this.vacancy?.status === vacancyStatus.ACTIVE) {
          this.$router.push({ name: 'candidates', params: { id: this.vacancyId.toString() } });
        }
      });
    },
    next() {
      this.$router.push({ name: 'vacancy-publish', params: { vacancyId: this.vacancyId } });
    },
    saveDraft() {
      Wizard.submitQuestions().then(() => {
        this.$router.push({ name: 'vacancy', params: { id: this.vacancyId.toString() } });
      });
    },
    back() {
      Wizard.pushPrevRoute(this.$router);
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.prepare());
  },
  beforeRouteUpdate(to, from, next) {
    this.prepare();
    next();
  },
};
</script>

<style lang="scss" scoped>
.h-100 {
  height: 100%;
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .mobile-container {
    width: calc(100% + 24px);
    margin-left: -12px;
  }
}
</style>

<i18n locale="en" lang="yaml">
  step: "Job FAQ"
  questionNotSaved: You did not save the question
  continueEditing: Continue editing
  publish: Publish
  next: Next
  saveDraft: Save draft
  commonQuestionsTip: |-
    The format of the virtual interview does not offer the applicant the ability to ask questions.
    You can give answers to common questions about the company.
    The applicant will see them after completing the interview.
  lostInfoWarning: Changes not saved will be lost. Are you sure?
  continue: Continue editing
  goBack: Go back
  publishAfterAiCompile: Publish after compilation
  pleaseWaitAiLoaderTextVoice: 'Artificial intelligence is in the process of voicing questions for your job.
    We expect it to take 3-5 minutes, please wait... If necessary, you can go to other sections.
    The job will be available in the jobs section after completion of the preparation process'
</i18n>

<i18n locale="ru" lang="yaml">
  step: "Вопрос-ответ о вакансии"
  questionNotSaved: Вы не сохранили вопрос
  continueEditing: Продолжить редактирование
  publish: Опубликовать
  next: Далее
  saveDraft: Сохранить черновик
  commonQuestionsTip: |-
    Формат виртуального собеседования не подразумевает возможности задавать вопросы
    для соискателя. Вы можете дать ответы на типовые вопросы о компании.
    Соискатель увидит их после прохождения интервью.
  lostInfoWarning: Все изменения будут потеряны. Вы уверены?
  continue: Продолжить редактирование
  goBack: Назад
  publishAfterAiCompile: Опубликовать после компиляции
  pleaseWaitAiLoaderTextVoice: 'Искусственный интеллект в процессе озвучивания вопросов для вашей вакансии.
    Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости вы можете перейти в другие разделы.
    Вакансия будет доступна в разделе вакансий после завершения процесса подготовки.'
</i18n>
